import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useToast } from '@/hooks/use-toast';
import { ShieldCheck } from 'lucide-react';

interface MFAChallengeProps {
  onVerified: () => void;
  onCancel: () => void;
  onSwitchMethod?: () => void;
  hasOtherMethods?: boolean;
}

export default function MFAChallenge({ onVerified, onCancel, onSwitchMethod, hasOtherMethods }: MFAChallengeProps) {
  const [verifyCode, setVerifyCode] = useState('');
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    const factors = await supabase.auth.mfa.listFactors();
    if (factors.error) {
      toast({
        title: 'Fehler',
        description: factors.error.message,
        variant: 'destructive',
      });
      setLoading(false);
      return;
    }

    const totpFactor = factors.data.totp[0];
    if (!totpFactor) {
      toast({
        title: 'Kein 2FA-Faktor gefunden',
        description: 'Bitte richte zuerst 2FA ein.',
        variant: 'destructive',
      });
      setLoading(false);
      return;
    }

    const challenge = await supabase.auth.mfa.challenge({ factorId: totpFactor.id });
    if (challenge.error) {
      toast({
        title: 'Challenge fehlgeschlagen',
        description: challenge.error.message,
        variant: 'destructive',
      });
      setLoading(false);
      return;
    }

    const verify = await supabase.auth.mfa.verify({
      factorId: totpFactor.id,
      challengeId: challenge.data.id,
      code: verifyCode,
    });

    if (verify.error) {
      toast({
        title: 'Code ungültig',
        description: 'Bitte überprüfe den Code und versuche es erneut.',
        variant: 'destructive',
      });
      setVerifyCode('');
      setLoading(false);
      return;
    }

    toast({
      title: 'Erfolgreich verifiziert',
      description: 'Willkommen im Admin-Bereich',
    });
    onVerified();
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mb-4">
            <ShieldCheck className="w-6 h-6 text-primary" />
          </div>
          <CardTitle className="text-2xl">2FA-Verifizierung</CardTitle>
          <CardDescription>
            Gib den 6-stelligen Code aus deiner Authenticator-App ein
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleVerify} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="mfa-code">Bestätigungscode</Label>
              <Input
                id="mfa-code"
                type="text"
                inputMode="numeric"
                pattern="[0-9]{6}"
                maxLength={6}
                value={verifyCode}
                onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, ''))}
                placeholder="000000"
                autoComplete="one-time-code"
                autoFocus
                required
              />
            </div>
            <Button type="submit" className="w-full" disabled={loading || verifyCode.length !== 6}>
              {loading ? 'Überprüfen...' : 'Bestätigen'}
            </Button>
          </form>
          <div className="mt-4 flex flex-col items-center gap-2">
            {hasOtherMethods && onSwitchMethod && (
              <button
                type="button"
                className="text-sm text-muted-foreground hover:text-primary transition-colors"
                onClick={onSwitchMethod}
              >
                Andere Methode verwenden
              </button>
            )}
            <button
              type="button"
              className="text-sm text-muted-foreground hover:text-primary transition-colors"
              onClick={onCancel}
            >
              Abmelden
            </button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
